<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAlert } from 'dashboard/composables';
import authAPI from 'dashboard/api/auth';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { SESSION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const sessions = ref([]);
const loading = ref(false);
const loaded = ref(false);
// Encerrar saia na hora, num botão pequeno: um toque errado derrubava o
// próprio celular. Agora confirma com o nome do aparelho.
const confirmDialogRef = ref(null);
const sessionToRevoke = ref(null);
const revokingId = ref(null);

const relativeTime = dateStr => {
  if (!dateStr) return '';
  return formatDistanceToNow(parseISO(dateStr), {
    addSuffix: true,
    locale: ptBR,
  });
};

const isUnknown = val => !val || val === 'Unknown' || val === 'Unknown Browser';

const deviceIcon = session => {
  const name = (session.device_name || '').toLowerCase();
  if (
    name.includes('iphone') ||
    name.includes('android') ||
    name.includes('mobile')
  ) {
    return 'i-ri-smartphone-line';
  }
  if (name.includes('ipad') || name.includes('tablet')) {
    return 'i-ri-tablet-line';
  }
  return 'i-ri-computer-line';
};

// O ' on ' fixo aparecia em ingles ("Chrome 120 on macOS").
const sessionLabel = session => {
  const browser = isUnknown(session.browser_name)
    ? ''
    : [session.browser_name, session.browser_version].filter(Boolean).join(' ');
  const platform = isUnknown(session.platform_name)
    ? ''
    : session.platform_name;
  if (browser && platform) {
    return t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.DEVICE', {
      browser,
      platform,
    });
  }
  return (
    browser ||
    platform ||
    t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.UNKNOWN_DEVICE')
  );
};

const locationLabel = session => {
  const parts = [];
  if (session.city) parts.push(session.city);
  if (session.country) parts.push(session.country);
  return parts.join(', ');
};

const fetchSessions = async () => {
  loading.value = true;
  try {
    const { data } = await authAPI.getSessions();
    sessions.value = data;
    loaded.value = true;
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const askToRevoke = session => {
  sessionToRevoke.value = session;
  confirmDialogRef.value?.open();
};

const revokeSession = async session => {
  revokingId.value = session.id;
  try {
    await authAPI.revokeSession(session.id);
    sessions.value = sessions.value.filter(s => s.id !== session.id);
    AnalyticsHelper.track(SESSION_EVENTS.REVOKED_FROM_PROFILE);
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_ERROR'));
  } finally {
    revokingId.value = null;
  }
};

const confirmRevoke = async () => {
  const session = sessionToRevoke.value;
  confirmDialogRef.value?.close();
  sessionToRevoke.value = null;
  if (session) await revokeSession(session);
};

onMounted(fetchSessions);
</script>

<template>
  <div class="flex flex-col gap-3">
    <template v-if="loading && !sessions.length">
      <div
        v-for="placeholder in 2"
        :key="placeholder"
        class="h-[76px] rounded-2xl border border-n-slate-4 bg-n-alpha-1 animate-pulse motion-reduce:animate-none"
        data-testid="sessions-skeleton"
        aria-hidden="true"
      />
      <span class="sr-only" role="status">
        {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.LOADING') }}
      </span>
    </template>
    <p
      v-else-if="loaded && !sessions.length"
      class="m-0 text-body-main text-n-slate-11"
      data-testid="sessions-empty"
    >
      {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.EMPTY') }}
    </p>
    <div
      v-for="session in sessions"
      :key="session.id"
      class="flex items-center justify-between gap-4 rounded-2xl border border-n-slate-4 bg-n-background p-4"
    >
      <div class="flex items-start gap-3">
        <Icon
          :icon="deviceIcon(session)"
          class="size-5 mt-0.5 text-n-slate-10 flex-shrink-0"
        />
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <span class="text-heading-3 text-n-slate-12">
              {{ sessionLabel(session) }}
            </span>
            <span
              v-if="session.current"
              class="rounded-full bg-n-teal-3 px-2 py-0.5 text-caption text-n-teal-11"
            >
              {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CURRENT') }}
            </span>
          </div>
          <span
            v-if="locationLabel(session)"
            class="text-body-b3 text-n-slate-11"
          >
            {{ locationLabel(session) }}
          </span>
          <span
            v-if="session.last_activity_at"
            class="text-body-b3 text-n-slate-10"
          >
            {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.LAST_ACTIVE') }}
            {{ relativeTime(session.last_activity_at) }}
          </span>
        </div>
      </div>
      <Button
        v-if="!session.current"
        type="button"
        faded
        sm
        class="!h-11 shrink-0"
        :label="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE')"
        color="ruby"
        :is-loading="revokingId === session.id"
        :disabled="revokingId === session.id"
        :data-testid="`revoke-session-${session.id}`"
        @click="askToRevoke(session)"
      />
    </div>
    <Dialog
      ref="confirmDialogRef"
      type="alert"
      :title="
        $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CONFIRM.TITLE', {
          device: sessionToRevoke ? sessionLabel(sessionToRevoke) : '',
        })
      "
      :description="
        $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CONFIRM.DESCRIPTION')
      "
      :confirm-button-label="
        $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CONFIRM.YES')
      "
      :cancel-button-label="
        $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CONFIRM.NO')
      "
      @confirm="confirmRevoke"
    />
  </div>
</template>
